import { type ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';

/**
 * Protected route wrapper for the patient portal.
 * Verifies authentication and portal_user_id linkage.
 */
export function PortalProtectedRoute({ children }: { children: ReactNode }) {
  const { status, user } = useAuth();
  const location = useLocation();
  const [hasPortalAccess, setHasPortalAccess] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    if (status === 'authenticated' && user) {
      const checkPortalAccess = async () => {
        try {
          const { data: patients, error } = await supabase
            .from('patients')
            .select('id')
            .eq('portal_user_id', user.id)
            .eq('status', 'active')
            .limit(1);

          if (error) {
            console.error('Error checking portal access:', error);
            setHasPortalAccess(false);
          } else {
            setHasPortalAccess(Boolean(patients && patients.length > 0));
          }
        } catch (err) {
          console.error('Exception checking portal access:', err);
          setHasPortalAccess(false);
        } finally {
          setChecking(false);
        }
      };

      void checkPortalAccess();
    } else if (status === 'unauthenticated') {
      setChecking(false);
    }
  }, [status, user]);

  if (status === 'initializing' || (status === 'authenticated' && checking)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-gray-900 dark:border-white" />
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return <Navigate to="/portal/login" state={{ from: location }} replace />;
  }

  if (hasPortalAccess === false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="mx-auto max-w-md rounded-lg bg-white p-6 text-center shadow dark:bg-gray-800">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Access Denied
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            No patient portal access found. Please contact your clinic for assistance.
          </p>
          <button
            onClick={async () => {
              await supabase.auth.signOut();
              window.location.href = '/portal/login';
            }}
            className="mt-4 rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
